import { Button } from "@/components/primitives/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/primitives/drawer";

import { Separator } from "@/components/primitives/separator";

import { useNavigationState } from "@/zustand/NavigationState";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";

import { EllipsisVertical, Trash2, MessagesSquare, Crown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getAssignableRoles } from "./ChatMemberMenu";
import { useState } from "react";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/primitives/radio-group";
import { Label } from "@/components/primitives/label";
import PromoteToOwnerDialog from "./menu/PromoteToOwnerDialog";
import RemoveMemberFromChatDialog from "./menu/RemoveMemberFromChatDialog";

type ChatMemberMobileMenuProps = {
  username: string;
  memberRole: MemberRole;
  onStartP2PChat: () => void;

  onChangeMemberRole: (newRole: MemberRole) => void;
};

export default function ChatMemberMobileMenu({
  username,
  memberRole,
  onStartP2PChat,

  onChangeMemberRole,
}: ChatMemberMobileMenuProps) {
  const { t } = useTranslation();

  const [openDialog, setOpenDialog] = useState<
    "promote-to-owner" | "delete" | null
  >(null);

  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );
  const activeChatType = useNavigationState((state) => state.activeChatType);

  if (activeChatType !== ChatType.GROUP) {
    return null;
  }

  // if user has MEMBER ROLE - can only start DMs
  if (activeChatRoleAsMember === MemberRole.MEMBER) {
    return (
      <Button
        onClick={onStartP2PChat}
        className="ml-auto size-4 lg:cursor-pointer"
        size="icon"
        variant="ghost"
      >
        <MessagesSquare />
      </Button>
    );
  }

  const assignableRoles = getAssignableRoles(
    activeChatRoleAsMember,
    memberRole,
  );

  return (
    <>
      <Drawer>
        <DrawerTrigger className="ml-auto">
          <Button
            asChild
            className="size-5 cursor-pointer"
            size="icon"
            variant="ghost"
          >
            <EllipsisVertical
              className="shrink-0 border-none outline-none"
              size={20}
              strokeWidth={2}
            />
          </Button>
        </DrawerTrigger>
        <DrawerContent aria-describedby="chat-menu" className="px-4 pb-4">
          <DrawerHeader className="text-center text-sm font-light text-secondary">
            {username}
          </DrawerHeader>
          <DrawerTitle />
          <DrawerDescription />

          <DrawerClose asChild>
            <Button
              variant="link"
              className="mb-1 flex items-center justify-start gap-3 text-base text-foreground no-underline hover:no-underline"
              onClick={onStartP2PChat}
            >
              <MessagesSquare strokeWidth={2} className="size-4" />
              {t("directMessage")}
            </Button>
          </DrawerClose>

          {/* CHANGE ROLE OPTIONS - only if active user has higher role than the member */}
          {assignableRoles.length > 1 && (
            <div className="flex flex-1 flex-col gap-0 px-3">
              <Separator className="mb-3" />
              <DrawerTitle className="mb-3">{t("changeRole")}</DrawerTitle>

              <RadioGroup
                value={memberRole.toString()}
                onValueChange={(value) =>
                  onChangeMemberRole(parseInt(value) as MemberRole)
                }
              >
                {assignableRoles.map((role) => (
                  <DrawerClose key={role} asChild>
                    <div className="my-1 flex items-center justify-start gap-5">
                      {" "}
                      <RadioGroupItem
                        value={role.toString()}
                        id={role.toString()}
                      />
                      <Label htmlFor={role.toString()}>
                        {t(
                          role === MemberRole.ADMIN
                            ? "adminRole"
                            : role === MemberRole.MODERATOR
                              ? "moderatorRole"
                              : "memberRole",
                        )}
                      </Label>
                    </div>
                  </DrawerClose>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* PROMOTE TO OWNER OPTION */}
          {memberRole === MemberRole.ADMIN && (
            <>
              <Separator className="my-3" />

              <DrawerClose asChild>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setOpenDialog("promote-to-owner");
                  }}
                  className="flex justify-start text-secondary"
                >
                  <Crown className="mr-2 size-4" />
                  {t("transferOwnership.promoteToOwner")}
                </Button>
              </DrawerClose>
            </>
          )}

          {/* DELETE OPTION - only if active user has higher role than the member */}
          {activeChatRoleAsMember < memberRole && (
            <>
              <Separator className="mt-3" />
              <DrawerClose asChild>
                <Button
                  variant="link"
                  className="mb-1 flex items-center justify-start gap-3 text-base text-destructive no-underline hover:no-underline"
                  onClick={() => {
                    setOpenDialog("delete");
                  }}
                >
                  <Trash2 strokeWidth={2} className="size-4" />
                  {t("remove")}
                </Button>
              </DrawerClose>
            </>
          )}
        </DrawerContent>
      </Drawer>

      {/* Promote to Owner Dialog */}
      <PromoteToOwnerDialog
        username={username}
        open={openDialog === "promote-to-owner"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "promote-to-owner" : null);
        }}
      />
      {/* Remove Member From Chat Dialog */}
      <RemoveMemberFromChatDialog
        username={username}
        open={openDialog === "delete"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete" : null);
        }}
      />
    </>
  );
}
